import { useState, useEffect } from 'react';
import { X, Mail } from 'lucide-react';
import { createDistributionList } from '../../services/api';
import toast from 'react-hot-toast';

interface CreateDistributionListModalProps {
  clientId: string;
  onClose: () => void;
  onCreated: () => void;
}

export default function CreateDistributionListModal({ clientId, onClose, onCreated }: CreateDistributionListModalProps) {
  const [displayName, setDisplayName] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [alias, setAlias] = useState('');
  const [creating, setCreating] = useState(false);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !creating) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose, creating]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!displayName.trim()) {
      toast.error('Display name is required');
      return;
    }

    if (!emailAddress.trim() || !emailAddress.includes('@')) {
      toast.error('A valid email address is required');
      return;
    }

    try {
      setCreating(true);
      const result = await createDistributionList(clientId, {
        displayName: displayName.trim(),
        emailAddress: emailAddress.trim(),
        alias: alias.trim() || undefined,
      });

      if (result.success) {
        toast.success(result.message || `Distribution list "${displayName}" created successfully`);
        onCreated();
      } else {
        toast.error(result.error || 'Failed to create distribution list');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create distribution list';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={creating ? undefined : onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-card rounded-lg shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              <h2 className="text-lg font-semibold text-foreground">Create Distribution List</h2>
            </div>
            <button
              onClick={onClose}
              disabled={creating}
              className="p-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-accent disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Info banner */}
          <div className="mx-4 mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-xs text-amber-800 dark:text-amber-200">
              This uses Exchange Online and may take 30-60 seconds to complete.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Display Name */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Display Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g., Sales Team"
                className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={creating}
                autoFocus
              />
            </div>

            {/* Email Address */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                placeholder="e.g., sales@contoso.com"
                className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={creating}
              />
            </div>

            {/* Alias */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Alias
              </label>
              <input
                type="text"
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
                placeholder={emailAddress.split('@')[0] || 'auto-generated from email'}
                className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={creating}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Leave blank to auto-generate from the email address.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={creating}
                className="flex-1 px-4 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-accent transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating || !displayName.trim() || !emailAddress.trim()}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {creating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Creating...
                  </>
                ) : (
                  'Create Distribution List'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
